import React, { useState, useEffect } from 'react';
import { getCurrentUser } from '../services/authService';
import { saveReceipt, Receipt, getCarriers, Carrier, getLanes, Lane } from '../services/dbService';
import { extractBolNumber, extractPdfData } from '../services/emailService';
import { generatePdfReceipt } from '../services/receiptService';
import { PDFDocument } from 'pdf-lib';
import { debugLogPdfContent } from '../services/debugService';

const ManualReceipt: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [extractedBol, setExtractedBol] = useState<string | null>(null);
  const [carriers, setCarriers] = useState<Carrier[]>([]);
  const [lanes, setLanes] = useState<Lane[]>([]);
  const [selectedLane, setSelectedLane] = useState<string>('');
  
  // Form state
  const [receiptData, setReceiptData] = useState<Partial<Receipt>>({
    bolNumber: '',
    shipmentNumber: '',
    carrierId: '',
    carrierName: '',
    origin: '',
    destination: '',
    amount: 140, // Default amount
    date: new Date(),
    status: 'pending',
  });
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [fetchedCarriers, fetchedLanes] = await Promise.all([
          getCarriers(),
          getLanes()
        ]);
        setCarriers(fetchedCarriers);
        setLanes(fetchedLanes);
      } catch (err: any) {
        setError(err.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    if (name === 'carrierId') {
      const selectedCarrier = carriers.find(c => c.id === value);
      setReceiptData({
        ...receiptData,
        carrierId: value,
        carrierName: selectedCarrier?.name || ''
      });
    } else {
      setReceiptData({
        ...receiptData,
        [name]: value
      });
    }
  };
  
  const handleLaneChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const laneId = e.target.value;
    setSelectedLane(laneId);
    
    if (laneId) {
      const lane = lanes.find(l => l.id === laneId);
      if (lane) {
        setReceiptData({
          ...receiptData,
          laneId: lane.id,
          origin: lane.origin,
          destination: lane.destination,
          amount: lane.price
        });
      }
    }
  };
  
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setPdfFile(file);
      
      try {
        setLoading(true);
        // Read the PDF file
        const fileBuffer = await file.arrayBuffer();
        const pdfBytes = new Uint8Array(fileBuffer);
        
        console.log('---DEBUG: PDF PROCESSING START---');
        console.log('File name:', file.name);
        console.log('File size:', file.size, 'bytes');
        
        // Run debug logging
        await debugLogPdfContent(pdfBytes);
        
        // Extract data from PDF
        const pdfData = await extractPdfData(pdfBytes);
        console.log('Extracted PDF data:', pdfData);
        
        const bol = pdfData.bolNumber;
        setExtractedBol(bol);
        
        // Update receipt data with extracted information
        // But preserve origin and destination if they were set from a lane
        const newReceiptData = { ...receiptData };
        
        if (bol) {
          newReceiptData.bolNumber = bol;
          console.log('Extracted BOL:', bol);
        } else {
          console.warn('No BOL number found in PDF');
        }
        
        if (pdfData.shipmentNumber) {
          newReceiptData.shipmentNumber = pdfData.shipmentNumber;
          console.log('Extracted shipment number:', pdfData.shipmentNumber);
        } else {
          console.warn('No shipment number found in PDF');
        }
        
        // Only update origin/destination if not already set from a lane
        // Check if origin was set from a lane (lane selection comes before file upload)
        if (pdfData.origin && !selectedLane) {
          newReceiptData.origin = pdfData.origin;
          console.log('Extracted origin (no lane selected):', pdfData.origin);
        } else if (pdfData.origin) {
          console.log('Keeping lane origin instead of extracted:', pdfData.origin);
        } else {
          console.warn('No origin found in PDF');
        }
        
        // Only update destination if not already set from a lane
        if (pdfData.destination && !selectedLane) {
          newReceiptData.destination = pdfData.destination;
          console.log('Extracted destination (no lane selected):', pdfData.destination);
        } else if (pdfData.destination) {
          console.log('Keeping lane destination instead of extracted:', pdfData.destination);
        } else {
          console.warn('No destination found in PDF');
        }
        
        if (pdfData.deliveryDate) {
          newReceiptData.date = pdfData.deliveryDate;
          console.log('Extracted delivery date:', pdfData.deliveryDate);
        } else {
          console.warn('No delivery date found in PDF');
        }
        
        console.log('---DEBUG: PDF PROCESSING END---');
        
        setReceiptData(newReceiptData);
        
        // For compatibility with existing code
        try {
          const pdfDoc = await PDFDocument.load(pdfBytes);
          console.log(`PDF loaded successfully with ${pdfDoc.getPageCount()} pages`);
        } catch (pdfErr) {
          console.warn('Could not parse PDF for additional metadata', pdfErr);
        }
      } catch (err: any) {
        console.error('Error processing PDF', err);
        setError('Failed to process PDF: ' + err.message);
      } finally {
        setLoading(false);
      }
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError(null);
      
      const user = getCurrentUser();
      if (!user) {
        throw new Error('User not authenticated');
      }
      
      if (!receiptData.bolNumber) {
        throw new Error('BOL number is required');
      }
      
      if (!receiptData.carrierId) {
        throw new Error('Carrier is required');
      }
      
      // Create the receipt
      const receipt: Receipt = {
        ...receiptData as Receipt,
        userId: user.uid,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      // Save to database
      await saveReceipt(receipt);
      
      // Reset form
      setReceiptData({
        bolNumber: '',
        shipmentNumber: '',
        carrierId: '',
        carrierName: '',
        origin: '',
        destination: '',
        amount: 140,
        date: new Date(),
        status: 'pending',
      });
      setPdfFile(null);
      setExtractedBol(null);
      setSelectedLane('');
      setSuccess(true);
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      console.error('Error saving receipt:', err);
      setError(err.message || 'Failed to save receipt');
    } finally {
      setLoading(false);
    }
  };
  
  const handlePreview = async () => {
    try {
      setLoading(true);
      
      const user = getCurrentUser();
      if (!user) {
        throw new Error('User not authenticated');
      }
      
      if (!receiptData.bolNumber) {
        throw new Error('BOL number is required');
      }
      
      if (!receiptData.carrierId) {
        throw new Error('Carrier is required');
      }
      
      // Create a temporary receipt for preview
      const tempReceipt: Receipt = {
        ...receiptData as Receipt,
        userId: user.uid,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      // Generate and download the PDF
      const pdfBytes = await generatePdfReceipt(tempReceipt);
      
      // Create blob and download
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      
      const a = document.createElement('a');
      a.href = url;
      a.download = `Preview_Receipt_${tempReceipt.bolNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Error generating preview:', err);
      setError(err.message || 'Failed to generate preview');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="manual-receipt-page">
      <div className="page-header">
        <h1>NFI Lumper Receipt Creator | Create Manual Receipt</h1>
      </div>
      
      {loading && <div className="loading">Processing...</div>}
      
      {error && <div className="error-message">{error}</div>}
      
      {success && (
        <div className="success-message">
          Receipt saved successfully!
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="receipt-form">
        <div className="form-section">
          <h2>Upload PDF (Optional)</h2>
          <div className="file-upload-container">
            <input
              type="file"
              accept=".pdf"
              onChange={handleFileChange}
              className="file-input"
              title="Upload a PDF file to extract BOL number and other data"
              aria-label="Upload PDF file"
            />
            <div className="file-status">
              {pdfFile && (
                <div className="file-name">
                  {pdfFile.name}
                  {extractedBol && (
                    <div className="extraction-result">
                      <span className="success">✓</span> Extracted BOL: {extractedBol}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className="form-section">
          <h2>Receipt Information</h2>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="date">Date</label>
              <input
                type="date"
                id="date"
                name="date"
                value={receiptData.date instanceof Date 
                  ? receiptData.date.toISOString().split('T')[0]
                  : new Date().toISOString().split('T')[0]}
                onChange={(e) => setReceiptData({
                  ...receiptData,
                  date: new Date(e.target.value)
                })}
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="carrierId">Carrier</label>
              <select
                id="carrierId"
                name="carrierId"
                value={receiptData.carrierId || ''}
                onChange={handleInputChange}
                required
                aria-label="Select carrier for receipt"
              >
                <option value="">Select Carrier</option>
                {carriers.map(carrier => (
                  <option key={carrier.id} value={carrier.id}>
                    {carrier.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="bolNumber">BOL Number*</label>
              <input
                type="text"
                id="bolNumber"
                name="bolNumber"
                value={receiptData.bolNumber || ''}
                onChange={handleInputChange}
                placeholder="BOL Number"
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="shipmentNumber">Shipment Number</label>
              <input
                type="text"
                id="shipmentNumber"
                name="shipmentNumber"
                value={receiptData.shipmentNumber || ''}
                onChange={handleInputChange}
                placeholder="Shipment Number"
              />
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="lane">Lane (Optional)</label>
              <select
                id="lane"
                name="lane"
                value={selectedLane}
                onChange={handleLaneChange}
                title="Select a lane to auto-fill origin, destination, and price"
                aria-label="Select lane to auto-fill receipt information"
              >
                <option value="">Select Lane</option>
                {lanes.map(lane => (
                  <option key={lane.id} value={lane.id}>
                    {lane.origin} → {lane.destination} (${lane.price})
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="origin">Origin</label>
              <input
                type="text"
                id="origin"
                name="origin"
                value={receiptData.origin || ''}
                onChange={handleInputChange}
                placeholder="Origin"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="destination">Destination</label>
              <input
                type="text"
                id="destination"
                name="destination"
                value={receiptData.destination || ''}
                onChange={handleInputChange}
                placeholder="Destination"
              />
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="amount">Amount ($)</label>
              <input
                type="number"
                id="amount"
                name="amount"
                value={receiptData.amount || 0}
                onChange={(e) => setReceiptData({
                  ...receiptData,
                  amount: parseFloat(e.target.value)
                })}
                min="0"
                step="0.01"
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="status">Status</label>
              <select
                id="status"
                name="status"
                value={receiptData.status || 'pending'}
                onChange={handleInputChange}
                required
                title="Select the payment status of the receipt"
                aria-label="Select receipt payment status"
              >
                <option value="pending">Pending</option>
                <option value="paid">Paid</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>
        </div>
        
        <div className="form-actions">
          <button 
            type="button"
            onClick={handlePreview}
            className="preview-btn"
            disabled={loading || !receiptData.bolNumber || !receiptData.carrierId}
          >
            Preview Receipt
          </button>
          <button 
            type="submit" 
            className="save-btn"
            disabled={loading || !receiptData.bolNumber || !receiptData.carrierId}
          >
            Save Receipt
          </button>
        </div>
      </form>
    </div>
  );
};

export default ManualReceipt; 